/**
 * Pin CSS cascade-layer order at the very first <style> in <head>.
 *
 * Three layered stylesheets cohabit at runtime:
 *   - `@picsart/design-system` foundation — injected by `useFoundation` via
 *     `document.head.prepend()`, declaring DS layer order with `cascade.atoms`
 *     near the front of its sequence.
 *   - `@picsart/design-system` per-component CSS (Button, etc.) — bundled by
 *     Vite from JS imports, declared in `@layer cascade.atoms`.
 *   - Tailwind v4 — `@import "tailwindcss"` adds `properties / theme / base /
 *     components / utilities`. `@layer base` carries the preflight reset
 *     including `button { background-color: transparent }`.
 *
 * The cascade is ordered by **first** declaration of each layer name in
 * document order. Because DS prepends its declaration after React mount, DS's
 * order wins by default — placing `cascade.atoms` (where DS Button skins
 * live) at a *lower* priority than Tailwind's `base`. Tailwind preflight then
 * strips DS Primary/Secondary skin backgrounds (the "Sign in to generate"
 * button rendering transparent instead of magenta).
 *
 * We fix this by inserting our own declaration as the first <style> in <head>
 * and keeping it there. The order below positions:
 *   base (Tailwind preflight) < foundation.* < cascade.atoms (DS skins) < utilities (Tailwind)
 *
 * Net result:
 *   - DS Button Primary skin overrides Tailwind preflight (Generate button
 *     paints magenta).
 *   - Tailwind utility classes still override DS skins when explicitly applied
 *     (e.g. `bg-transparent` on a close-icon button stays transparent).
 *
 * A MutationObserver re-prepends our <style> if anything else (such as
 * `useFoundation` running on a theme change) prepends in front of us.
 */

const PIN_ID = '__cascade-layer-pin__';

const ORDER =
  '@layer properties, theme, base, foundation.reset, foundation.primitives, foundation.themes, cascade.subAtoms, cascade.atoms, components, cascade.molecules, cascade.organisms, cascade.templates, utilities;';

if (typeof document !== 'undefined' && !document.getElementById(PIN_ID)) {
  const pin = document.createElement('style');
  pin.id = PIN_ID;
  pin.textContent = ORDER;
  document.head.prepend(pin);

  new MutationObserver(() => {
    if (document.head.firstElementChild !== pin) {
      document.head.prepend(pin);
    }
  }).observe(document.head, { childList: true });
}

export {};
